<template>
  <b-modal
    id="oee-details-modal"
    v-model="visible"
    title="Detalhamento do Cálculo de OEE"
    size="lg"
    centered
    hide-footer
    body-class="p-0"
    modal-class="oee-details-modal-wrapper"
  >
    <template #modal-header>
      <div class="d-flex justify-content-between align-items-center w-100 py-50 px-1">
        <div>
          <h5 class="modal-title font-weight-bold text-dark">
            <feather-icon icon="TrendingUpIcon" class="mr-50 text-primary" />
            Detalhamento do OEE
          </h5>
          <span class="text-muted font-small-3">{{ title }}</span>
        </div>
        <button type="button" class="close" @click="visible = false" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    </template>

    <div class="oee-modal-body">
      <!-- Tabs Navigation -->
      <div class="oee-tabs-nav d-flex mb-2">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          :class="['oee-tab-btn', { active: activeTab === tab.id }]"
          @click="activeTab = tab.id"
        >
          <feather-icon :icon="tab.icon" class="mr-50" size="14" />
          {{ tab.label }}
        </button>
      </div>

      <!-- Tab Content: OEE Geral -->
      <div v-if="activeTab === 'oee'" class="tab-pane-content fade-in">
        <div class="text-center mb-2">
          <span class="text-muted uppercase font-weight-bold font-small-2 d-block mb-25">OEE GLOBAL DO PERÍODO</span>
          <h1 class="oee-result-value font-weight-bolder text-primary">
            {{ formatPercent(calculatedOee) }}%
          </h1>
          <div class="d-flex justify-content-center align-items-center mt-50">
            <span :class="['badge badge-pill', getKpiBadgeClass(calculatedOee)]">
              {{ getOeeStatusText(calculatedOee) }}
            </span>
          </div>
        </div>

        <h6 class="section-title text-dark font-weight-bold mb-1">Como este número é formado?</h6>
        <p class="text-muted font-small-3 mb-2">
          O OEE (Overall Equipment Effectiveness) é o produto dos três fatores abaixo: Disponibilidade, Performance e Qualidade.
        </p>

        <!-- Equation Flowchart -->
        <div class="oee-equation-flow d-flex align-items-stretch justify-content-between mb-2">
          <div class="equation-card cursor-pointer" @click="activeTab = 'availability'">
            <div class="card-header-small bg-light-primary text-primary">Disponibilidade</div>
            <div class="card-body-small">
              <span class="card-value">{{ formatPercent(calculatedAvailability) }}%</span>
              <span class="card-hint">Tempo Trabalhado / Programado</span>
            </div>
          </div>

          <div class="equation-operator">&times;</div>

          <div class="equation-card cursor-pointer" @click="activeTab = 'performance'">
            <div class="card-header-small bg-light-warning text-warning">Performance</div>
            <div class="card-body-small">
              <span class="card-value">{{ formatPercent(calculatedPerformance) }}%</span>
              <span class="card-hint">Ciclo Padrão / Ciclo Real</span>
            </div>
          </div>

          <div class="equation-operator">&times;</div>

          <div class="equation-card cursor-pointer" @click="activeTab = 'quality'">
            <div class="card-header-small bg-light-success text-success">Qualidade</div>
            <div class="card-body-small">
              <span class="card-value">{{ formatPercent(calculatedQuality) }}%</span>
              <span class="card-hint">Peças Boas / Total Produzido</span>
            </div>
          </div>

          <div class="equation-operator">=</div>

          <div class="equation-card result-card">
            <div class="card-header-small bg-primary text-white">OEE Final</div>
            <div class="card-body-small">
              <span class="card-value text-primary">{{ formatPercent(calculatedOee) }}%</span>
              <span class="card-hint">Resultado Geral</span>
            </div>
          </div>
        </div>

        <div class="card bg-light-secondary border-0 p-1">
          <div class="d-flex align-items-start">
            <feather-icon icon="InfoIcon" class="text-secondary mr-75 mt-25" size="16" />
            <div class="font-small-3 text-secondary">
              <strong>Dica:</strong> Clique em qualquer um dos três componentes acima para ver o detalhamento matemático completo e a distribuição dos tempos correspondentes.
            </div>
          </div>
        </div>
      </div>

      <!-- Tab Content: Disponibilidade -->
      <div v-if="activeTab === 'availability'" class="tab-pane-content fade-in">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h6 class="section-title text-dark font-weight-bold mb-0">Cálculo de Disponibilidade</h6>
          <span class="badge badge-light-primary font-weight-bolder font-medium-1">
            {{ formatPercent(calculatedAvailability) }}%
          </span>
        </div>

        <!-- Math Equation -->
        <div class="math-box mb-2">
          <div class="math-label">Fórmula:</div>
          <div class="math-formula">
            <span class="math-variable">Disponibilidade</span>
            <span class="math-equal">=</span>
            <div class="math-fraction">
              <div class="math-numerator">Tempo Trabalhado</div>
              <div class="math-denominator">Tempo Programado</div>
            </div>
            <span class="math-multiplier">&times; 100</span>
          </div>
          <div class="math-formula mt-1">
            <span class="math-variable">Cálculo:</span>
            <span class="math-equal">=</span>
            <div class="math-fraction">
              <div class="math-numerator">{{ formatNumber(availDetails.workedSeconds) }}s <span class="text-muted">({{ formatSecondsToHours(availDetails.workedSeconds) }})</span></div>
              <div class="math-denominator">{{ formatNumber(availDetails.programmedSeconds) }}s <span class="text-muted">({{ formatSecondsToHours(availDetails.programmedSeconds) }})</span></div>
            </div>
            <span class="math-equal">=</span>
            <span class="math-result text-primary font-weight-bold">{{ formatPercent(calculatedAvailability) }}%</span>
          </div>
        </div>

        <!-- Visual Timeline / Time allocation -->
        <h6 class="text-dark font-weight-bold mb-50">Distribuição do Tempo no Período</h6>
        <p class="text-muted font-small-3 mb-1">
          Veja como o tempo total disponível foi alocado entre trabalho produtivo e perdas por paradas:
        </p>

        <div class="time-progress-container mb-2">
          <!-- Total Time Bar -->
          <div class="time-bar-group">
            <div class="d-flex justify-content-between font-small-2 mb-25">
              <span class="font-weight-bold text-dark">1. Tempo Total Disponibilizado</span>
              <span class="text-secondary">{{ formatSecondsToHours(availDetails.totalSeconds) }} ({{ formatNumber(availDetails.totalSeconds) }}s)</span>
            </div>
            <b-progress class="progress-bar-sm mb-1" :max="availDetails.totalSeconds || 1">
              <b-progress-bar :value="availDetails.totalSeconds" class="bg-secondary" />
            </b-progress>
          </div>

          <!-- Programmed Time vs Planned Stops Bar -->
          <div class="time-bar-group">
            <div class="d-flex justify-content-between font-small-2 mb-25">
              <span class="font-weight-bold text-dark">2. Tempo Programado vs. Fora de Turno/Paradas Planejadas</span>
              <span class="text-secondary">Programado: {{ formatSecondsToHours(availDetails.programmedSeconds) }}</span>
            </div>
            <b-progress class="progress-bar-sm mb-1" :max="availDetails.totalSeconds || 1">
              <b-progress-bar :value="Math.max(0, availDetails.programmedSeconds)" class="bg-primary" v-b-tooltip.hover title="Tempo Programado" />
              <b-progress-bar :value="availDetails.offShiftPlannedStopSeconds" class="bg-light-secondary" style="background-image: repeating-linear-gradient(45deg, rgba(0,0,0,0.05), rgba(0,0,0,0.05) 10px, transparent 10px, transparent 20px);" v-b-tooltip.hover title="Fora de Turno / Paradas Planejadas" />
            </b-progress>
          </div>

          <!-- Worked Time vs Unplanned Stops Bar -->
          <div class="time-bar-group">
            <div class="d-flex justify-content-between font-small-2 mb-25">
              <span class="font-weight-bold text-dark">3. Tempo Trabalhado vs. Paradas Não Planejadas</span>
              <span class="text-secondary">Efetivo: {{ formatSecondsToHours(availDetails.workedSeconds) }}</span>
            </div>
            <b-progress class="progress-bar-sm mb-1" :max="Math.max(1, availDetails.programmedSeconds || 1)">
              <b-progress-bar :value="availDetails.workedSeconds" class="bg-success" v-b-tooltip.hover title="Tempo Efetivamente Trabalhado" />
              <b-progress-bar :value="availDetails.unplannedStopSeconds" class="bg-danger" v-b-tooltip.hover title="Paradas Não Planejadas (Perda)" />
            </b-progress>
          </div>
        </div>

        <!-- Legend and Details List -->
        <div class="row">
          <div class="col-12 col-md-6">
            <div class="detail-item-list">
              <div class="detail-row">
                <span class="detail-bullet bg-secondary"></span>
                <span class="detail-label">Tempo Total:</span>
                <span class="detail-value">{{ formatSecondsToHours(availDetails.totalSeconds) }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-bullet bg-light-secondary border"></span>
                <span class="detail-label">Fora de Turno / Planejado:</span>
                <span class="detail-value text-danger">- {{ formatSecondsToHours(availDetails.offShiftPlannedStopSeconds) }}</span>
              </div>
              <div class="detail-row highlight-row">
                <span class="detail-bullet bg-primary"></span>
                <span class="detail-label">Tempo Programado:</span>
                <span class="detail-value font-weight-bold">{{ formatSecondsToHours(availDetails.programmedSeconds) }}</span>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6">
            <div class="detail-item-list">
              <div class="detail-row">
                <span class="detail-bullet bg-danger"></span>
                <span class="detail-label">Paradas Não Planejadas:</span>
                <span class="detail-value text-danger">- {{ formatSecondsToHours(availDetails.unplannedStopSeconds) }}</span>
              </div>
              <div class="detail-row highlight-row success-row">
                <span class="detail-bullet bg-success"></span>
                <span class="detail-label">Tempo Trabalhado:</span>
                <span class="detail-value font-weight-bold">{{ formatSecondsToHours(availDetails.workedSeconds) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab Content: Performance -->
      <div v-if="activeTab === 'performance'" class="tab-pane-content fade-in">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h6 class="section-title text-dark font-weight-bold mb-0">Cálculo de Performance</h6>
          <span class="badge badge-light-warning font-weight-bolder font-medium-1 text-warning">
            {{ formatPercent(calculatedPerformance) }}%
          </span>
        </div>

        <!-- Math Equation -->
        <div class="math-box mb-2">
          <div class="math-label">Fórmula:</div>
          <div class="math-formula">
            <span class="math-variable">Performance</span>
            <span class="math-equal">=</span>
            <div class="math-fraction">
              <div class="math-numerator">Tempo Produtivo Esperado</div>
              <div class="math-denominator">Tempo Produtivo Real</div>
            </div>
            <span class="math-multiplier">&times; 100</span>
          </div>
          <div class="math-formula mt-1">
            <span class="math-variable">Cálculo:</span>
            <span class="math-equal">=</span>
            <div class="math-fraction">
              <div class="math-numerator">{{ formatNumber(perfDetails.expectedProductiveSeconds) }}s <span class="text-muted">({{ formatSecondsToHours(perfDetails.expectedProductiveSeconds) }})</span></div>
              <div class="math-denominator">{{ formatNumber(perfDetails.productiveSeconds) }}s <span class="text-muted">({{ formatSecondsToHours(perfDetails.productiveSeconds) }})</span></div>
            </div>
            <span class="math-equal">=</span>
            <span class="math-result text-warning font-weight-bold">{{ formatPercent(calculatedPerformance) }}%</span>
          </div>
          <div v-if="!isGlobal" class="font-small-2 text-muted mt-50">
            * O Tempo Produtivo Esperado é calculado como: <strong>Quantidade Produzida ({{ formatNumber(perfDetails.totalProducedQuantity) }}) &times; Ciclo Padrão ({{ perfDetails.standardCycle }}s)</strong> = {{ formatNumber(perfDetails.expectedProductiveSeconds) }}s.
          </div>
        </div>

        <!-- Performance details table -->
        <div class="card bg-light-secondary border-0 p-1">
          <h6 class="text-dark font-weight-bold font-small-3 mb-50">Resumo de Métricas de Performance</h6>
          <div class="row text-dark font-small-3">
            <div class="col-6 mb-50">
              <span class="text-muted">Quantidade Total Produzida:</span>
            </div>
            <div class="col-6 mb-50 text-right font-weight-bold">
              {{ formatNumber(perfDetails.totalProducedQuantity) }} peças
            </div>
            <div class="col-6 mb-50">
              <span class="text-muted">Tempo de Operação Produtiva (Real):</span>
            </div>
            <div class="col-6 mb-50 text-right font-weight-bold">
              {{ formatSecondsToHours(perfDetails.productiveSeconds) }} ({{ formatNumber(perfDetails.productiveSeconds) }}s)
            </div>
            <div class="col-6 mb-50">
              <span class="text-muted">Tempo Efetivamente Necessário (Esperado):</span>
            </div>
            <div class="col-6 mb-50 text-right font-weight-bold text-primary">
              {{ formatSecondsToHours(perfDetails.expectedProductiveSeconds) }} ({{ formatNumber(perfDetails.expectedProductiveSeconds) }}s)
            </div>
            <div class="col-6" v-if="perfDetails.performanceLossSeconds > 0">
              <span class="text-muted">Perda estimada por lentidão:</span>
            </div>
            <div class="col-6 text-right font-weight-bold text-danger" v-if="perfDetails.performanceLossSeconds > 0">
              {{ formatSecondsToHours(perfDetails.performanceLossSeconds) }} ({{ formatNumber(perfDetails.performanceLossSeconds) }}s)
            </div>
          </div>
        </div>
      </div>

      <!-- Tab Content: Qualidade -->
      <div v-if="activeTab === 'quality'" class="tab-pane-content fade-in">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h6 class="section-title text-dark font-weight-bold mb-0">Cálculo de Qualidade</h6>
          <span class="badge badge-light-success font-weight-bolder font-medium-1 text-success">
            {{ formatPercent(calculatedQuality) }}%
          </span>
        </div>

        <!-- Math Equation -->
        <div class="math-box mb-2">
          <div class="math-label">Fórmula:</div>
          <div class="math-formula">
            <span class="math-variable">Qualidade</span>
            <span class="math-equal">=</span>
            <div class="math-fraction">
              <div class="math-numerator">Quantidade de Peças Boas</div>
              <div class="math-denominator">Quantidade Total Produzida</div>
            </div>
            <span class="math-multiplier">&times; 100</span>
          </div>
          <div class="math-formula mt-1">
            <span class="math-variable">Cálculo:</span>
            <span class="math-equal">=</span>
            <div class="math-fraction">
              <div class="math-numerator">{{ formatNumber(qualDetails.goodQuantity) }} <span class="text-muted">peças</span></div>
              <div class="math-denominator">{{ formatNumber(qualDetails.totalProducedQuantity) }} <span class="text-muted">peças</span></div>
            </div>
            <span class="math-equal">=</span>
            <span class="math-result text-success font-weight-bold">{{ formatPercent(calculatedQuality) }}%</span>
          </div>
        </div>

        <!-- Production Quality Split -->
        <h6 class="text-dark font-weight-bold mb-50">Distribuição da Qualidade das Peças</h6>
        <p class="text-muted font-small-3 mb-1">
          Proporção de peças aprovadas em relação a refugos e retrabalhos:
        </p>

        <div class="mb-2">
          <b-progress class="progress-bar-lg mb-50" :max="qualDetails.totalProducedQuantity || 1">
            <b-progress-bar :value="qualDetails.goodQuantity" class="bg-success" v-b-tooltip.hover title="Peças Boas" />
            <b-progress-bar :value="qualDetails.refuseQuantity" class="bg-danger" v-b-tooltip.hover title="Refugo (Descartadas)" />
            <b-progress-bar :value="qualDetails.reworkQuantity" class="bg-warning" v-b-tooltip.hover title="Retrabalho (Recondicionadas)" />
          </b-progress>

          <div class="d-flex justify-content-between font-small-2 text-muted">
            <span>Boas: {{ formatPercent((qualDetails.goodQuantity / (qualDetails.totalProducedQuantity || 1)) * 100) }}%</span>
            <span v-if="qualDetails.refuseQuantity > 0">Refugo: {{ formatPercent((qualDetails.refuseQuantity / (qualDetails.totalProducedQuantity || 1)) * 100) }}%</span>
            <span v-if="qualDetails.reworkQuantity > 0">Retrabalho: {{ formatPercent((qualDetails.reworkQuantity / (qualDetails.totalProducedQuantity || 1)) * 100) }}%</span>
          </div>
        </div>

        <!-- Detailed Metrics -->
        <div class="row">
          <div class="col-12 col-md-6 mb-1 mb-md-0">
            <div class="card bg-light-success border-0 p-1 h-100 text-center d-flex flex-column justify-content-center">
              <span class="text-success font-weight-bold font-small-2 uppercase">Aprovadas (Boas)</span>
              <h2 class="font-weight-bolder text-success my-25">{{ formatNumber(qualDetails.goodQuantity) }}</h2>
              <span class="text-muted font-small-3">Peças em conformidade com o padrão</span>
            </div>
          </div>
          <div class="col-12 col-md-6">
            <div class="card bg-light-danger border-0 p-1 h-100 text-center d-flex flex-column justify-content-center">
              <span class="text-danger font-weight-bold font-small-2 uppercase">Refugadas / Retrabalhadas</span>
              <h2 class="font-weight-bolder text-danger my-25">{{ formatNumber(qualDetails.refuseQuantity + qualDetails.reworkQuantity) }}</h2>
              <span class="text-muted font-small-3" v-if="qualDetails.qualityLossSeconds > 0">
                Tempo perdido produzindo refugo: <strong>{{ formatSecondsToHours(qualDetails.qualityLossSeconds) }}</strong>
              </span>
              <span class="text-muted font-small-3" v-else>
                Sem perdas temporais registradas por qualidade
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BProgress, BProgressBar } from "bootstrap-vue";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";

export default {
  name: "OeeDetailsModal",
  components: {
    BModal,
    BProgress,
    BProgressBar,
    FeatherIcon,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Global",
    },
    oee: {
      type: Number,
      default: 0,
    },
    availableComponent: {
      type: Number,
      default: 0,
    },
    performanceComponent: {
      type: Number,
      default: 0,
    },
    qualityComponent: {
      type: Number,
      default: 0,
    },
    details: {
      type: Object,
      default: () => ({}),
    },
    initialTab: {
      type: String,
      default: "oee",
    },
  },
  data() {
    return {
      activeTab: "oee",
      tabs: [
        { id: "oee", label: "OEE Geral", icon: "ActivityIcon" },
        { id: "availability", label: "Disponibilidade", icon: "ClockIcon" },
        { id: "performance", label: "Performance", icon: "ZapIcon" },
        { id: "quality", label: "Qualidade", icon: "CheckCircleIcon" },
      ],
    };
  },
  computed: {
    visible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    availDetails() {
      return this.details?.availability || {
        totalSeconds: 0,
        offShiftPlannedStopSeconds: 0,
        programmedSeconds: 0,
        unplannedStopSeconds: 0,
        workedSeconds: 0,
      };
    },
    perfDetails() {
      return this.details?.performance || {
        productiveSeconds: 0,
        totalProducedQuantity: 0,
        expectedProductiveSeconds: 0,
        performanceLossSeconds: 0,
        standardCycle: 0,
        actualCycle: 0,
      };
    },
    qualDetails() {
      return this.details?.quality || {
        totalProducedQuantity: 0,
        goodQuantity: 0,
        refuseQuantity: 0,
        reworkQuantity: 0,
        qualityLossSeconds: 0,
      };
    },
    calculatedAvailability() {
      const details = this.availDetails;
      if (details && details.programmedSeconds > 0) {
        return (details.workedSeconds / details.programmedSeconds) * 100;
      }
      return this.availableComponent;
    },
    calculatedPerformance() {
      const details = this.perfDetails;
      if (details && details.productiveSeconds > 0) {
        return (details.expectedProductiveSeconds / details.productiveSeconds) * 100;
      }
      return this.performanceComponent;
    },
    calculatedQuality() {
      const details = this.qualDetails;
      if (details && details.totalProducedQuantity > 0) {
        return (details.goodQuantity / details.totalProducedQuantity) * 100;
      }
      return this.qualityComponent;
    },
    calculatedOee() {
      if (this.details?.availability && this.details?.performance && this.details?.quality) {
        const avail = this.calculatedAvailability;
        const perf = this.calculatedPerformance;
        const qual = this.calculatedQuality;
        return (avail * perf * qual) / 10000;
      }
      return this.oee;
    },
    isGlobal() {
      return this.title.toLowerCase().includes("geral") || this.title.toLowerCase().includes("consolidado");
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.activeTab = this.initialTab || "oee";
      }
    },
    initialTab(val) {
      this.activeTab = val || "oee";
    },
  },
  methods: {
    formatPercent(val) {
      if (val == null || !Number.isFinite(val)) return "0.00";
      const num = Math.abs(val) <= 1 ? val * 100 : val;
      return num.toFixed(2);
    },
    formatNumber(val) {
      if (val == null || !Number.isFinite(val)) return "0";
      return Number(val).toLocaleString("pt-BR", { maximumFractionDigits: 2 });
    },
    formatSecondsToHours(totalSeconds) {
      if (totalSeconds == null || !Number.isFinite(totalSeconds)) return "00:00:00";
      const isNegative = totalSeconds < 0;
      const absSeconds = Math.abs(totalSeconds);

      const hours = Math.floor(absSeconds / 3600);
      const minutes = Math.floor((absSeconds % 3600) / 60);
      const seconds = Math.round(absSeconds % 60);

      const sign = isNegative ? "-" : "";
      
      if (hours > 0) {
        return `${sign}${hours}h ${minutes.toString().padStart(2, "0")}m ${seconds.toString().padStart(2, "0")}s`;
      }
      if (minutes > 0) {
        return `${sign}${minutes}m ${seconds.toString().padStart(2, "0")}s`;
      }
      return `${sign}${seconds}s`;
    },
    getKpiBadgeClass(value) {
      if (value < 80) return "badge-light-danger";
      if (value >= 80 && value < 90) return "badge-light-warning";
      return "badge-light-success";
    },
    getOeeStatusText(value) {
      if (value < 80) return "Abaixo do Esperado";
      if (value >= 80 && value < 90) return "Bom";
      return "Excelente (Classe Mundial)";
    },
  },
};
</script>

<style lang="scss" scoped>
.oee-modal-body {
  padding: 20px;
}

// Tabs Styling
.oee-tabs-nav {
  background-color: #f8f9fa;
  padding: 4px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  width: 100%;
}

.oee-tab-btn {
  flex: 1;
  border: none;
  background: transparent;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.25s ease;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #495057;
    background-color: rgba(0, 0, 0, 0.02);
  }

  &.active {
    color: #9f5714;
    background-color: #ffffff;
    box-shadow: 0 2px 8px rgba(159, 87, 20, 0.12);
  }
}

// Animation
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

.oee-result-value {
  font-size: 3.5rem;
  letter-spacing: -1px;
  line-height: 1;
}

.section-title {
  font-size: 14px;
  border-left: 3px solid #9f5714;
  padding-left: 8px;
}

// Equation Flowchart
.oee-equation-flow {
  gap: 8px;
  align-items: center;

  @media (max-width: 767.98px) {
    flex-direction: column;
    gap: 12px;
  }
}

.equation-card {
  flex: 1;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
  transition: all 0.2s ease;
  text-align: center;
  box-shadow: 0 2px 6px rgba(0,0,0,0.02);

  &.cursor-pointer:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
    border-color: #cbd5e1;
  }

  &.result-card {
    border-color: rgba(159, 87, 20, 0.2);
    background: linear-gradient(to bottom, #ffffff, #fffdfa);
  }

  @media (max-width: 767.98px) {
    width: 100%;
  }
}

.card-header-small {
  padding: 6px 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-body-small {
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .card-value {
    font-size: 18px;
    font-weight: 800;
    color: #1e293b;
    line-height: 1.2;
  }

  .card-hint {
    font-size: 9px;
    color: #64748b;
    margin-top: 4px;
    line-height: 1.1;
  }
}

.equation-operator {
  font-size: 20px;
  font-weight: 700;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;

  @media (max-width: 767.98px) {
    transform: rotate(90deg);
    padding: 2px 0;
  }
}

// Math fraction styling
.math-box {
  background-color: #f8f9fa;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  padding: 16px;
}

.math-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.math-formula {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #334155;
  flex-wrap: wrap;
  gap: 8px;
}

.math-variable {
  font-weight: 700;
  color: #1e293b;
}

.math-equal {
  color: #64748b;
  font-weight: 600;
}

.math-fraction {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 6px;
}

.math-numerator {
  border-bottom: 1.5px solid #475569;
  padding-bottom: 2px;
  font-weight: 500;
}

.math-denominator {
  padding-top: 2px;
  font-weight: 500;
}

.math-multiplier {
  font-weight: 600;
  color: #64748b;
}

.math-result {
  font-size: 16px;
}

// Progress bars
.progress-bar-sm {
  height: 8px;
  border-radius: 4px;
  background-color: #f1f5f9;
}

.progress-bar-lg {
  height: 18px;
  border-radius: 9px;
  background-color: #f1f5f9;
}

// Detail rows list
.detail-item-list {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 8px 12px;
}

.detail-row {
  display: flex;
  align-items: center;
  padding: 6px 0;
  font-size: 12px;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }

  &.highlight-row {
    margin-top: 4px;
    border-top: 1.5px solid #e2e8f0;
    padding-top: 8px;
  }

  &.success-row {
    color: #15803d;
  }
}

.detail-bullet {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
}

.detail-label {
  color: #475569;
  flex-grow: 1;
}

.detail-value {
  font-family: monospace;
  font-weight: 600;
  color: #0f172a;
}

.cycle-card {
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.01);
}
</style>
